import React, { useState, useEffect } from 'react';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, getDay, isPast, isToday, startOfWeek, endOfWeek, parseISO } from 'date-fns';
import api from '../api/axios';

const SpecificAvailabilityV2 = () => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [dates, setDates] = useState([]);
  const [slotEditorDate, setSlotEditorDate] = useState(null);
  const [blockedSlots, setBlockedSlots] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingDate, setSavingDate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  useEffect(() => { fetchDates(); }, [currentMonth]);

  useEffect(() => {
    if (slotEditorDate?.date) fetchBlockedSlots(slotEditorDate.date);
  }, [slotEditorDate?.date]);

  const fetchDates = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const response = await api.get(
        `/specific-availability/admin/dates?startDate=${format(monthStart, 'yyyy-MM-dd')}&endDate=${format(monthEnd, 'yyyy-MM-dd')}`
      );
      setDates(response.data.dates || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch dates');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const fetchBlockedSlots = async (date) => {
    try {
      const response = await api.get(`/specific-availability/admin/dates/${date}/blocked-slots`);
      setBlockedSlots(prev => ({
        ...prev,
        // Normalize HH:MM:SS → HH:MM so comparisons against generated time24 values work
        [date]: (response.data.blockedSlots || []).map(slot => ({
          ...slot,
          timeSlot: String(slot.timeSlot).substring(0, 5)
        }))
      }));
    } catch (err) {
      console.error('Fetch blocked slots error:', err);
    }
  };

  const getDateInfo = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return dates.find(d => d.date === dateStr);
  };

  const generateTimeSlots = (startTime, endTime) => {
    const slots = [];
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    let h = startHour, m = startMin;
    while (h < endHour || (h === endHour && m < endMin)) {
      const period = h >= 12 ? 'PM' : 'AM';
      const display = h > 12 ? h - 12 : h === 0 ? 12 : h;
      slots.push({
        time24: `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`,
        time12: `${display}:${String(m).padStart(2, '0')} ${period}`
      });
      m += 30;
      if (m >= 60) { m = 0; h += 1; }
    }
    return slots;
  };

  const handleEnableDate = async (day) => {
    if (isPast(day) && !isToday(day)) return;
    const dateStr = format(day, 'yyyy-MM-dd');
    const existingDate = getDateInfo(day);
    const willEnable = !existingDate?.isEnabled;

    try {
      setSavingDate(dateStr);
      setError('');
      const slots = generateTimeSlots('10:00', '18:00');
      await api.post('/specific-availability/admin/dates', {
        date: dateStr,
        startTime: '10:00',
        endTime: '18:00',
        isEnabled: willEnable,
        enabledTimeSlots: slots.map(s => s.time24)
      });
      setSuccess(`Date ${willEnable ? 'enabled (10 AM–6 PM)' : 'disabled'}!`);
      await fetchDates(true); // silent — preserves scroll position
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update date');
    } finally {
      setSavingDate(null);
    }
  };

  const handleEnableEntireWeek = async () => {
    try {
      setSaving(true);
      setError('');
      const weekStart = startOfWeek(currentMonth, { weekStartsOn: 1 });
      const weekEnd = endOfWeek(currentMonth, { weekStartsOn: 1 });
      const futureDays = eachDayOfInterval({ start: weekStart, end: weekEnd })
        .filter(day => !isPast(day) || isToday(day));
      const dateStrings = futureDays.map(d => format(d, 'yyyy-MM-dd'));
      const slots = generateTimeSlots('10:00', '18:00');

      await api.post('/specific-availability/admin/dates/bulk', {
        dates: dateStrings,
        startTime: '10:00',
        endTime: '18:00',
        isEnabled: true,
        enabledTimeSlots: slots.map(s => s.time24)
      });

      setSuccess(`Enabled ${dateStrings.length} days with 10 AM–6 PM default hours!`);
      await fetchDates(true);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to enable week');
    } finally {
      setSaving(false);
    }
  };

  const openSlotEditor = (day) => {
    const dateInfo = getDateInfo(day);
    if (!dateInfo?.isEnabled) return;
    setSlotEditorDate(dateInfo);
  };

  const closeSlotEditor = () => setSlotEditorDate(null);

  const handleToggleTimeSlot = async (timeSlot) => {
    if (!slotEditorDate) return;
    const date = slotEditorDate.date;
    const blocked = blockedSlots[date] || [];
    const isBlocked = blocked.some(b => b.timeSlot === timeSlot);

    // Optimistic update — instant visual feedback, revert on failure
    setBlockedSlots(prev => ({
      ...prev,
      [date]: isBlocked
        ? (prev[date] || []).filter(b => b.timeSlot !== timeSlot)
        : [...(prev[date] || []), { timeSlot, reason: 'admin_blocked' }]
    }));

    try {
      if (isBlocked) {
        await api.delete(`/specific-availability/admin/dates/${date}/unblock-slot/${timeSlot}`);
      } else {
        await api.post(`/specific-availability/admin/dates/${date}/block-slot`, {
          timeSlot,
          reason: 'admin_blocked'
        });
      }
    } catch (err) {
      // Revert on failure
      await fetchBlockedSlots(date);
      setError(err.response?.data?.error || 'Failed to update time slot');
    }
  };

  return (
    <div>
      {/* Compact header + quick actions in one row */}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900 leading-tight">Calendar Availability</h1>
          <p className="text-xs text-gray-500 mt-0.5">Click a date to enable/disable · click ✎ on an enabled date to edit individual slots</p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <button
            type="button"
            onClick={handleEnableEntireWeek}
            disabled={saving}
            className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap transition-colors"
          >
            {saving ? 'Enabling…' : 'Enable This Week'}
          </button>
          <span className="text-xs text-gray-500 whitespace-nowrap">Mon–Sun · 10 AM–6 PM</span>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 text-red-600 p-2.5 rounded mb-3 text-sm flex justify-between items-center">
          <span>{error}</span>
          <button type="button" onClick={() => setError('')} className="ml-3 text-red-400 hover:text-red-600 text-lg leading-none">×</button>
        </div>
      )}
      {success && (
        <div className="bg-green-50 text-green-700 p-2.5 rounded mb-3 text-sm">{success}</div>
      )}

      <div className="bg-white rounded-lg shadow-md p-4">
        {/* Month navigation */}
        <div className="flex justify-between items-center mb-3">
          <button
            type="button"
            onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
            className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 text-sm transition-colors"
          >
            ← Prev
          </button>
          <h2 className="text-base font-semibold text-gray-900">{format(currentMonth, 'MMMM yyyy')}</h2>
          <button
            type="button"
            onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
            className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 text-sm transition-colors"
          >
            Next →
          </button>
        </div>

        {/* Compact legend — single line */}
        <div className="mb-3 flex items-center gap-4 text-xs text-gray-600 flex-wrap">
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-green-500 inline-block"></span>Enabled</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded border border-gray-400 bg-white inline-block"></span>Not set</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-gray-200 inline-block"></span>Past</span>
          <span className="flex items-center gap-1.5 text-blue-600 font-medium"><span className="w-3 h-3 rounded border-2 border-blue-500 inline-block"></span>Today</span>
        </div>

        {loading ? (
          <div className="text-center py-10 text-gray-500 text-sm">Loading calendar…</div>
        ) : (
          <>
            {/* Day-of-week headers */}
            <div className="grid grid-cols-7 gap-1 mb-1">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                <div key={d} className="text-center text-xs font-semibold text-gray-500 py-1">{d}</div>
              ))}
            </div>

            {/* Calendar cells */}
            <div className="grid grid-cols-7 gap-1">
              {Array.from({ length: getDay(monthStart) }).map((_, i) => (
                <div key={`empty-${i}`} className="aspect-square" />
              ))}

              {daysInMonth.map((day, idx) => {
                const dateInfo = getDateInfo(day);
                const dateStr = format(day, 'yyyy-MM-dd');
                const isDisabled = isPast(day) && !isToday(day);
                const isSavingThis = savingDate === dateStr;

                let cellClass = 'border border-gray-300 bg-white text-gray-700 cursor-pointer hover:bg-blue-50';
                if (isDisabled) {
                  cellClass = 'border border-gray-200 bg-gray-100 text-gray-400 cursor-default';
                } else if (dateInfo?.isEnabled) {
                  cellClass = 'border border-green-600 bg-green-500 text-white cursor-pointer hover:bg-green-600';
                }

                const todayRing = isToday(day) && !dateInfo?.isEnabled
                  ? ' ring-2 ring-blue-500 ring-offset-1'
                  : '';

                return (
                  <div
                    key={idx}
                    className={`relative aspect-square rounded-lg transition-all duration-100 flex flex-col items-center justify-center select-none group ${cellClass}${todayRing}`}
                    onClick={() => !isDisabled && !isSavingThis && handleEnableDate(day)}
                    role="button"
                    tabIndex={isDisabled ? -1 : 0}
                    onKeyDown={(e) => e.key === 'Enter' && !isDisabled && handleEnableDate(day)}
                    title={
                      isDisabled ? 'Past date'
                      : dateInfo?.isEnabled ? 'Click to disable'
                      : 'Click to enable'
                    }
                  >
                    {isSavingThis ? (
                      <span className="text-xs opacity-60">…</span>
                    ) : (
                      <>
                        <span className="text-sm font-semibold leading-none">{format(day, 'd')}</span>
                        {dateInfo?.isEnabled && (
                          <span className="text-[9px] mt-0.5 opacity-80 leading-none">10–6PM</span>
                        )}
                        {/* Edit icon — only visible on hover, only for enabled dates */}
                        {dateInfo?.isEnabled && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); openSlotEditor(day); }}
                            className="absolute top-0.5 right-0.5 w-5 h-5 flex items-center justify-center rounded text-white/60 hover:text-white hover:bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity text-xs leading-none"
                            title="Edit individual time slots"
                          >
                            ✎
                          </button>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* ── Slot editor modal ── */}
      {slotEditorDate && (
        <div
          className="fixed inset-0 bg-black/50 z-[200] flex items-center justify-center p-4"
          onClick={closeSlotEditor}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="flex justify-between items-start mb-1">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {format(parseISO(slotEditorDate.date), 'EEEE, MMMM d, yyyy')}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">Click any slot to toggle availability</p>
              </div>
              <button
                type="button"
                onClick={closeSlotEditor}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none ml-4 flex-shrink-0"
              >
                ×
              </button>
            </div>

            {/* Legend */}
            <div className="flex gap-4 text-xs text-gray-600 mb-4 mt-3 flex-wrap">
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-500 inline-block"></span>Available</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-gray-400 inline-block"></span>Admin disabled</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-orange-400 inline-block"></span>Buffer</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-500 inline-block"></span>Booked</span>
            </div>

            {/* Slot grid — 3 columns, no scrolling needed for a full 9 AM–6 PM day (18 slots) */}
            <div className="grid grid-cols-3 gap-2">
              {generateTimeSlots(slotEditorDate.startTime, slotEditorDate.endTime).map(({ time24, time12 }) => {
                const slotData = (blockedSlots[slotEditorDate.date] || []).find(b => b.timeSlot === time24);
                const isBooked = slotData?.reason === 'booked';
                const isBuffer = slotData?.reason === 'buffer_before' || slotData?.reason === 'buffer_after';
                const isAdminBlocked = slotData?.reason === 'admin_blocked';
                const isActionable = !isBooked && !isBuffer;

                let slotClass = 'bg-green-100 text-green-800 hover:bg-green-200 cursor-pointer';
                if (isBooked) {
                  slotClass = 'bg-red-100 text-red-700 cursor-default';
                } else if (isBuffer) {
                  slotClass = 'bg-orange-100 text-orange-700 cursor-default';
                } else if (isAdminBlocked) {
                  slotClass = 'bg-gray-200 text-gray-700 hover:bg-gray-300 cursor-pointer';
                }

                // Build tooltip content for booked/buffer slots
                let tooltipContent = null;
                if ((isBooked || isBuffer) && slotData?.clientName) {
                  const lines = [slotData.clientName];
                  if (slotData.serviceName) lines.push(slotData.serviceName);
                  if (slotData.durationMinutes) lines.push(`${slotData.durationMinutes} min`);
                  if (slotData.clientPhone) lines.push(slotData.clientPhone);
                  if (slotData.appointmentNotes) lines.push(`Note: ${slotData.appointmentNotes}`);
                  tooltipContent = lines.join('\n');
                }

                return (
                  <div key={time24} className="relative group">
                    <button
                      type="button"
                      onClick={() => isActionable && handleToggleTimeSlot(time24)}
                      disabled={!isActionable}
                      title={tooltipContent || undefined}
                      className={`w-full px-2 py-2.5 text-xs rounded-md text-center transition-colors ${slotClass}`}
                    >
                      <div className="font-medium">{time12}</div>
                      {isBooked && (
                        <div className="text-[10px] mt-0.5 truncate font-medium">
                          {slotData?.clientName || 'Booked'}
                        </div>
                      )}
                      {isBuffer && (
                        <div className="text-[10px] opacity-70 mt-0.5">
                          {slotData?.clientName ? `Buffer (${slotData.clientName.split(' ')[0]})` : 'Buffer'}
                        </div>
                      )}
                    </button>
                    {/* Hover popover for booked slots with full details */}
                    {isBooked && slotData?.clientName && (
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 z-50 hidden group-hover:block w-48 bg-gray-900 text-white text-[11px] rounded-lg p-2.5 shadow-xl pointer-events-none">
                        <div className="font-semibold mb-1">{slotData.clientName}</div>
                        {slotData.serviceName && <div className="opacity-80">{slotData.serviceName}</div>}
                        {slotData.durationMinutes && <div className="opacity-80">{slotData.durationMinutes} min</div>}
                        {slotData.clientPhone && <div className="opacity-80">{slotData.clientPhone}</div>}
                        {slotData.appointmentNotes && (
                          <div className="opacity-70 mt-1 border-t border-white/20 pt-1 italic">{slotData.appointmentNotes}</div>
                        )}
                        {/* Arrow pointing down */}
                        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="mt-5 pt-4 border-t border-gray-100 flex justify-end">
              <button
                type="button"
                onClick={closeSlotEditor}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpecificAvailabilityV2;
